import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { X, User, Wallet, FileSignature, History, ShieldCheck, AlertTriangle } from "lucide-react";

// Iter 133 — Compact admin-only recipient overview. Opened by clicking the
// recipient name in the Payouts Pending list. Shows just enough for the
// admin to verify who they're paying:
//   • name + verified status
//   • on-file wallet (public address only when chain matches — Iter 132)
//   • originating contract (id + agreed monthly volume)
//   • the recipient's other payouts (history)
// PII gated: endpoint is admin-only. No private keys, ever.

const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—");

function Field({ label, value, testId }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">{label}</div>
      <div className="text-xs font-mono text-[#E8ECF4]" data-testid={testId}>{value ?? "—"}</div>
    </div>
  );
}

export default function RecipientOverviewModal({ payoutId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!payoutId) return;
    setLoading(true);
    api.get(`/admin/payouts/${payoutId}/recipient-overview`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [payoutId]);
  if (!payoutId) return null;

  const r = data?.recipient;
  const w = data?.wallet;
  const c = data?.contract;
  const hist = data?.history || [];

  const walletBlock = () => {
    if (!w) return null;
    if (w.matches_payout && w.address) {
      return (
        <div className="border border-[#2DD4A7]/40 bg-[#2DD4A7]/[0.05] p-3 space-y-1" data-testid="recipient-overview-wallet-match">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]"><Wallet className="w-3 h-3" />On-file payout wallet</div>
          <div className="text-xs font-mono text-[#E8ECF4]">{w.coin} · {w.network}</div>
          <div className="text-[11px] font-mono break-all text-[#E8ECF4]" data-testid="recipient-overview-wallet-address">{w.address}</div>
          {w.tag && <div className="text-[10px] text-[#8B97AE]">Tag: {w.tag}</div>}
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">Matches this payout's chain</div>
        </div>
      );
    }
    return (
      <div className="border border-[#F5B841]/60 bg-[#F5B841]/[0.08] p-3 space-y-1" data-testid="recipient-overview-wallet-mismatch">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]"><AlertTriangle className="w-3 h-3" />Wallet chain mismatch</div>
        <div className="text-xs font-mono text-[#E8ECF4]">
          Payout: <span className="text-[#F5B841]">{w.payout_coin || "—"} · {w.payout_network || "—"}</span>
          &nbsp;·&nbsp;On-file: <span className="text-[#8B97AE]">{w.coin || "—"} · {w.network || "—"}</span>
        </div>
        <div className="text-[10px] text-[#8B97AE]">No matching address on file. Confirm the correct address with the recipient — a wrong-chain send is irreversible.</div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="recipient-overview-modal" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#0B1220] border border-white/20 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#2DD4A7]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Recipient · admin overview</div>
              <div className="font-display font-bold text-base text-[#E8ECF4]" data-testid="recipient-overview-name">{r?.name || "—"}</div>
            </div>
          </div>
          <button data-testid="recipient-overview-close" onClick={onClose} className="text-[#8B97AE] hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="p-6 text-xs text-[#8B97AE]">Loading…</div>
        ) : !data ? (
          <div className="p-6 text-xs text-[#F26D6D]">Failed to load recipient overview.</div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kind" value={r?.kind} testId="recipient-overview-kind" />
              <Field label="ID" value={r?.id ? r.id.slice(0, 8) + "…" : "—"} testId="recipient-overview-id" />
              <Field label="Email" value={r?.email} testId="recipient-overview-email" />
              <Field label="Status" value={r?.status} testId="recipient-overview-status" />
              {r?.kind === "player" && (
                <>
                  <Field label="Verification tier" value={r?.verification_tier} testId="recipient-overview-tier" />
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">Per-vertical verified</div>
                    <div className="text-xs font-mono text-[#E8ECF4]" data-testid="recipient-overview-verticals">
                      {r?.vertical_verification && Object.keys(r.vertical_verification).length > 0
                        ? Object.entries(r.vertical_verification).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"
                        : "—"}
                    </div>
                  </div>
                </>
              )}
              {r?.kind === "streamer" && (
                <>
                  <Field label="Slug" value={r?.slug} testId="recipient-overview-slug" />
                  <Field label="Verticals enabled" value={(r?.enabled_verticals || []).join(", ") || "—"} testId="recipient-overview-verticals" />
                </>
              )}
            </div>

            {walletBlock()}

            {c && (
              <div className="border border-white/10 p-3 space-y-1" data-testid="recipient-overview-contract">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]"><FileSignature className="w-3 h-3" />Originating contract</div>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <Field label="Contract" value={c.id ? c.id.slice(0, 8) + "…" : "—"} testId="recipient-overview-contract-id" />
                  <Field label="Status" value={c.status} testId="recipient-overview-contract-status" />
                  <Field label="Vertical" value={c.vertical} testId="recipient-overview-contract-vertical" />
                  <Field label="Agreed monthly" value={`${fmt(c.agreed_monthly_volume)} USD`} testId="recipient-overview-contract-agreed" />
                </div>
              </div>
            )}

            <div className="border border-white/10 p-3 space-y-2" data-testid="recipient-overview-history">
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"><History className="w-3 h-3" />Payout history · {hist.length}</div>
              {hist.length === 0 ? (
                <div className="text-[11px] text-[#8B97AE]">No payouts on record.</div>
              ) : (
                <table className="w-full text-xs font-mono">
                  <thead className="text-[9px] uppercase tracking-widest text-[#8B97AE]">
                    <tr className="border-b border-white/5">
                      <th className="text-left p-1">Type</th>
                      <th className="text-right p-1">Amount</th>
                      <th className="text-left p-1">Currency</th>
                      <th className="text-left p-1">Status</th>
                      <th className="text-left p-1">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.map((h) => (
                      <tr key={h.id} className={`border-b border-white/5 last:border-b-0 ${h.id === payoutId ? "bg-[#2DD4A7]/[0.06]" : ""}`} data-testid={`recipient-overview-history-row-${h.id}`}>
                        <td className="p-1">{h.type}{h.id === payoutId && <span className="ml-1 text-[9px] text-[#2DD4A7]">·this</span>}</td>
                        <td className="p-1 text-right">{fmt(h.amount)}</td>
                        <td className="p-1">{h.currency}</td>
                        <td className="p-1">{h.status}</td>
                        <td className="p-1 text-[#8B97AE]">{(h.createdAt || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] pt-1 border-t border-white/10">
              <ShieldCheck className="w-3 h-3 text-[#2DD4A7]" />Admin-only view · public receiving address only · never any private key
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
